import { Search, User, ShoppingBag, Star, Facebook, Instagram, Twitter, ArrowRight, Heart } from "lucide-react";

// Bespoke pitch demo for molliescreations.com — front page + menu only, all links decorative.
// Modeled on the Fashiup PrestaShop template structure. No TraceElements/Emergent branding.

const PHOTOS = {
  heroCardigan: "https://molliescreations.com/cdn/shop/files/IMG_8350_1024x.jpg?v=1767665864",
  jackets: "https://molliescreations.com/cdn/shop/files/1CA885F0-C7F6-44F4-874F-C30AE2D09DC6_1024x.jpg?v=1768932523",
  purses: "https://molliescreations.com/cdn/shop/files/D9AC04AC-7C49-429A-96E7-C6AB081A3779_1024x.jpg?v=1762970515",
  blankets: "https://molliescreations.com/cdn/shop/files/IMG-1865_1024x.jpg?v=1756775034",
  remedies: "https://molliescreations.com/cdn/shop/files/4C41796C-E2D6-4072-8814-F2E0B7FAD883_1024x.jpg?v=1731536391",
};

const ACCENT = "#C4572E"; // warm terracotta — natural/handmade feel

const MENU = ["Home", "Shop", "Apparel", "Accessories", "Home & Living", "Natural Remedies", "Our Story", "Contact"];

const CATEGORIES = [
  { name: "Chunky Knit Jackets", count: "12 pieces", img: PHOTOS.jackets },
  { name: "Hand-Sewn Purses", count: "9 pieces", img: PHOTOS.purses },
  { name: "Heirloom Blankets", count: "7 pieces", img: PHOTOS.blankets },
  { name: "Natural Remedies", count: "5 blends", img: PHOTOS.remedies },
];

const PRODUCTS = [
  { name: "Heart Appliqué Chunky Cardigan", price: "$98.00", old: "$118.00", img: PHOTOS.heroCardigan, tag: "Best Seller" },
  { name: "Signature Knit Jacket", price: "$92.00", old: null, img: PHOTOS.jackets, tag: "New" },
  { name: "Everyday Hand-Sewn Purse", price: "$54.00", old: "$64.00", img: PHOTOS.purses, tag: null },
  { name: "Tayler's Pain Relief Blend", price: "$24.00", old: null, img: PHOTOS.remedies, tag: "Customer Favorite" },
];

const noop = (e) => e.preventDefault();

export default function MolliesDemo() {
  return (
    <div className="min-h-screen bg-[#FBF8F4] text-[#1C1917] font-jakarta" data-testid="mollies-demo">
      {/* ===== PROMO BAR ===== */}
      <div className="bg-[#1C1917] text-[#FBF8F4] text-center py-2.5 px-4 text-[11px] font-semibold uppercase tracking-[0.2em]" data-testid="mollies-promo-bar">
        Handmade in small batches · Free shipping on all orders over $75
      </div>

      {/* ===== HEADER + MENU (decorative) ===== */}
      <header className="sticky top-0 z-50 bg-[#FBF8F4]/90 backdrop-blur-lg border-b border-[#1C1917]/10">
        <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between gap-6">
          <a href="#" onClick={noop} className="flex items-center gap-2" data-testid="mollies-logo">
            <span className="h-9 w-9 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: ACCENT }}>
              <Heart size={16} fill="currentColor" />
            </span>
            <span className="font-extrabold text-xl tracking-tight">Mollie's <span style={{ color: ACCENT }}>Creations</span></span>
          </a>
          <nav className="hidden lg:flex items-center gap-7 text-[13px] font-semibold" data-testid="mollies-menu">
            {MENU.map((item) => (
              <a key={item} href="#" onClick={noop} className="hover:text-[#C4572E] transition-colors" data-testid={`mollies-menu-${item.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {item}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-5">
            <a href="#" onClick={noop} aria-label="Search" className="hover:text-[#C4572E] transition-colors" data-testid="mollies-icon-search"><Search size={19} /></a>
            <a href="#" onClick={noop} aria-label="Account" className="hover:text-[#C4572E] transition-colors hidden sm:block" data-testid="mollies-icon-account"><User size={19} /></a>
            <a href="#" onClick={noop} aria-label="Cart" className="relative hover:text-[#C4572E] transition-colors" data-testid="mollies-icon-cart">
              <ShoppingBag size={19} />
              <span className="absolute -top-2 -right-2 h-4.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold text-white flex items-center justify-center px-1" style={{ backgroundColor: ACCENT }}>0</span>
            </a>
          </div>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden" data-testid="mollies-hero">
        <div className="absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full opacity-20 blur-3xl" style={{ backgroundColor: ACCENT }} />
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 border border-[#1C1917]/15 bg-white px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: ACCENT }} data-testid="mollies-hero-badge">
              <Heart size={12} fill="currentColor" /> The Winter Knits Collection
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[64px] font-extrabold tracking-tight leading-[1.04]" data-testid="mollies-hero-headline">
              Handmade With Love, <span style={{ color: ACCENT }}>Worn With Pride.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg leading-relaxed text-[#1C1917]/65 max-w-xl" data-testid="mollies-hero-subheadline">
              Chunky knits, hand-sewn purses, heirloom blankets and natural remedies —
              crafted by our family, for yours. Every piece made by hand, never by machine.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#" onClick={noop} className="inline-flex items-center gap-2 text-white px-8 py-4 rounded-full text-xs font-extrabold uppercase tracking-[0.2em] transition-transform hover:-translate-y-0.5" style={{ backgroundColor: ACCENT, boxShadow: "0 12px 32px rgba(196,87,46,0.35)" }} data-testid="mollies-hero-cta">
                Shop the Collection <ArrowRight size={15} />
              </a>
              <a href="#" onClick={noop} className="inline-flex items-center gap-2 border border-[#1C1917]/20 px-8 py-4 rounded-full text-xs font-extrabold uppercase tracking-[0.2em] hover:border-[#C4572E] hover:text-[#C4572E] transition-colors" data-testid="mollies-hero-story">
                Our Story
              </a>
            </div>
            <div className="mt-9 flex items-center gap-6 text-xs text-[#1C1917]/55 font-semibold">
              <span className="flex items-center gap-1.5"><Star size={13} style={{ color: ACCENT }} fill="currentColor" /> 4.9 · 300+ reviews</span>
              <span>100% handmade</span>
              <span>Family owned</span>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="relative mx-auto max-w-[420px]">
              <div className="absolute -inset-4 rounded-[40px] opacity-15" style={{ backgroundColor: ACCENT }} />
              <img
                src={PHOTOS.heroCardigan}
                alt="Handmade chunky knit pink cardigan with heart appliqués"
                className="relative w-full rounded-[32px] object-cover shadow-2xl"
                style={{ aspectRatio: "4/5" }}
                data-testid="mollies-hero-image"
              />
              <div className="absolute -left-6 bottom-10 bg-white rounded-2xl shadow-xl px-5 py-4 flex items-center gap-3">
                <span className="h-10 w-10 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: ACCENT }}>
                  <Heart size={16} fill="currentColor" />
                </span>
                <div>
                  <div className="text-sm font-extrabold">Heart Cardigan</div>
                  <div className="text-xs text-[#1C1917]/55">Hand-knitted · $98.00</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CATEGORY SHOWCASE ===== */}
      <section className="py-16 lg:py-20 bg-white border-y border-[#1C1917]/8" data-testid="mollies-categories">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: ACCENT }}>Shop By Category</div>
              <h2 className="mt-3 text-3xl lg:text-4xl font-extrabold tracking-tight">Crafted Collections</h2>
            </div>
            <a href="#" onClick={noop} className="text-xs font-extrabold uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:text-[#C4572E] transition-colors" data-testid="mollies-categories-all">
              View All <ArrowRight size={14} />
            </a>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {CATEGORIES.map((c, i) => (
              <a key={c.name} href="#" onClick={noop} className="group block" data-testid={`mollies-category-${i}`}>
                <div className="overflow-hidden rounded-2xl">
                  <img src={c.img} alt={c.name} loading="lazy" className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div>
                    <div className="font-extrabold text-sm lg:text-base tracking-tight">{c.name}</div>
                    <div className="text-xs text-[#1C1917]/50 mt-0.5">{c.count}</div>
                  </div>
                  <span className="h-8 w-8 rounded-full border border-[#1C1917]/15 flex items-center justify-center transition-colors group-hover:text-white group-hover:border-transparent" style={{}}>
                    <ArrowRight size={14} className="group-hover:text-[#C4572E]" />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BEST SELLERS ===== */}
      <section className="py-16 lg:py-20" data-testid="mollies-products">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: ACCENT }}>Most Loved</div>
            <h2 className="mt-3 text-3xl lg:text-4xl font-extrabold tracking-tight">Best Sellers</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {PRODUCTS.map((p, i) => (
              <div key={p.name} className="group" data-testid={`mollies-product-${i}`}>
                <div className="relative overflow-hidden rounded-2xl bg-white">
                  {p.tag && (
                    <span className="absolute top-3 left-3 z-10 text-[10px] font-extrabold uppercase tracking-[0.15em] text-white px-3 py-1.5 rounded-full" style={{ backgroundColor: ACCENT }}>
                      {p.tag}
                    </span>
                  )}
                  <img src={p.img} alt={p.name} loading="lazy" className="w-full aspect-[4/5] object-cover transition-transform duration-500 group-hover:scale-105" />
                  <button
                    onClick={noop}
                    className="absolute bottom-3 left-3 right-3 py-3 rounded-full text-[11px] font-extrabold uppercase tracking-[0.18em] text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ backgroundColor: "#1C1917" }}
                    data-testid={`mollies-product-cart-${i}`}
                  >
                    Add to Cart
                  </button>
                </div>
                <div className="mt-4">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={12} style={{ color: ACCENT }} fill="currentColor" />
                    ))}
                  </div>
                  <div className="mt-2 font-bold text-sm tracking-tight">{p.name}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="font-extrabold text-sm" style={{ color: ACCENT }}>{p.price}</span>
                    {p.old && <span className="text-xs text-[#1C1917]/40 line-through">{p.old}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROMO BANNER ===== */}
      <section className="py-16 lg:py-20 px-6" data-testid="mollies-promo-banner">
        <div className="max-w-7xl mx-auto relative overflow-hidden rounded-[32px] bg-[#1C1917] text-[#FBF8F4]">
          <div className="absolute inset-0 opacity-25">
            <img src={PHOTOS.blankets} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1917] via-[#1C1917]/80 to-transparent" />
          <div className="relative px-8 lg:px-16 py-16 lg:py-20 max-w-2xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: "#E8A183" }}>Limited Season Offer</div>
            <h2 className="mt-4 text-3xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Save Up To 20% Off<br />Heirloom Blankets
            </h2>
            <p className="mt-4 text-sm lg:text-base text-[#FBF8F4]/70 leading-relaxed">
              Warmth that outlives trends. Hand-loomed throws and blankets, made to be handed down — not thrown out.
            </p>
            <a href="#" onClick={noop} className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full text-xs font-extrabold uppercase tracking-[0.2em] text-white transition-transform hover:-translate-y-0.5" style={{ backgroundColor: ACCENT }} data-testid="mollies-promo-cta">
              Shop Blankets <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </section>

      {/* ===== STORY STRIP ===== */}
      <section className="py-16 lg:py-20 bg-white border-y border-[#1C1917]/8" data-testid="mollies-story">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: ACCENT }}>Our Story</div>
          <h2 className="mt-4 text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight">
            Where family legacy meets natural living.
          </h2>
          <p className="mt-6 text-base lg:text-lg leading-relaxed text-[#1C1917]/65">
            Mollie's Creations was inspired by a daughter and a nephew — and born from a family's journey through cancer.
            Every stitch, seam and blend is a promise: effective, chemical-free, and made with intention.
            When you wear Mollie's, you carry a piece of that promise with you.
          </p>
          <a href="#" onClick={noop} className="mt-8 inline-flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.2em] hover:text-[#C4572E] transition-colors" data-testid="mollies-story-cta">
            Read Our Full Story <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* ===== FOOTER (decorative links) ===== */}
      <footer className="bg-[#1C1917] text-[#FBF8F4]" data-testid="mollies-footer">
        <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2">
              <span className="h-9 w-9 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: ACCENT }}>
                <Heart size={16} fill="currentColor" />
              </span>
              <span className="font-extrabold text-lg tracking-tight">Mollie's <span style={{ color: "#E8A183" }}>Creations</span></span>
            </div>
            <p className="mt-4 text-sm text-[#FBF8F4]/60 leading-relaxed">
              Handmade apparel, accessories, home goods and natural remedies — crafted by our family, for yours.
            </p>
            <div className="mt-5 flex items-center gap-3">
              {[Facebook, Instagram, Twitter].map((Icon, i) => (
                <a key={i} href="#" onClick={noop} aria-label="Social link" className="h-9 w-9 rounded-full border border-white/15 flex items-center justify-center hover:border-[#C4572E] hover:text-[#E8A183] transition-colors" data-testid={`mollies-social-${i}`}>
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#FBF8F4]/50 mb-4">Shop</div>
            <ul className="space-y-2.5 text-sm text-[#FBF8F4]/75">
              {["Chunky Knits", "Purses", "Blankets", "Natural Remedies", "Gift Cards"].map((l) => (
                <li key={l}><a href="#" onClick={noop} className="hover:text-[#E8A183] transition-colors">{l}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#FBF8F4]/50 mb-4">Help</div>
            <ul className="space-y-2.5 text-sm text-[#FBF8F4]/75">
              {["Shipping & Delivery", "Returns", "Care Instructions", "Size Guide", "Contact Us"].map((l) => (
                <li key={l}><a href="#" onClick={noop} className="hover:text-[#E8A183] transition-colors">{l}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#FBF8F4]/50 mb-4">Stay in the Loop</div>
            <p className="text-sm text-[#FBF8F4]/60 leading-relaxed">New drops, restocks and family news — once a month, never spammy.</p>
            <form onSubmit={noop} className="mt-4 flex" data-testid="mollies-newsletter">
              <input
                type="email" placeholder="Email address"
                className="flex-1 min-w-0 bg-white/10 border border-white/15 rounded-l-full px-4 py-3 text-sm outline-none placeholder:text-[#FBF8F4]/40"
                data-testid="mollies-newsletter-email"
              />
              <button className="px-5 rounded-r-full text-white text-xs font-extrabold uppercase tracking-widest" style={{ backgroundColor: ACCENT }} data-testid="mollies-newsletter-submit">
                Join
              </button>
            </form>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-[#FBF8F4]/50">© 2026 Mollie's Creations. All rights reserved.</span>
            <span className="text-xs text-[#FBF8F4]/50">Handmade with love, always.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
